import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2, HandHeart } from "lucide-react";

const CHARITY_PRINT_COLUMNS = [
  { key: "name", label: "الاسم", always: true },
  { key: "national_id", label: "رقم الهوية" },
  { key: "phone", label: "رقم الجوال" },
  { key: "gender", label: "الجنس" },
  { key: "nationality", label: "الجنسية" },
  { key: "campaign", label: "الحملة" },
  { key: "occurrence", label: "التسجيل رقم" },
];
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PrintColumnsDialog from "@/components/PrintColumnsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", national_id: "", phone: "", gender: "male", nationality: "", campaign_id: "" };

export default function CharityBeneficiaries() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [q, setQ] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const params = { portal: "charitable" };
      if (q) params.q = q;
      if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
      const [b, c] = await Promise.all([
        api.get("/beneficiaries", { params }),
        api.get("/campaigns", { params: { portal: "charitable" } }),
      ]);
      setItems(b.data); setCampaigns(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, campaignFilter]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/beneficiaries", { ...form, portal: "charitable" });
      toast.success(`تم التسجيل — التسجيل رقم ${res.data.occurrence}`);
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد الحذف؟")) return;
    try { await api.delete(`/beneficiaries/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const campaignName = useMemo(() => {
    const map = new Map(campaigns.map((c) => [c.id, `#${c.number} ${c.name}`]));
    return (id) => map.get(id) || "—";
  }, [campaigns]);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        title="مستفيدو البوابة الخيرية"
        subtitle={`العدد: ${items.length} — مع فحص تلقائي لمنع التكرار`}
        actions={
          <>
            <PrintColumnsDialog
              prefsKey="charitable_beneficiaries"
              columns={CHARITY_PRINT_COLUMNS}
              buildUrl={(cols) => {
                const qs = new URLSearchParams();
                qs.set("cols", cols.join(","));
                if (campaignFilter && campaignFilter !== "all") qs.set("campaign", campaignFilter);
                if (q) qs.set("q", q);
                return `/print/charitable/beneficiaries?${qs.toString()}`;
              }}
              testIdPrefix="charity-print"
            />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="charity-ben-add" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Plus className="h-4 w-4 ml-2" /> مستفيد جديد
                </Button>
              </DialogTrigger>
              <DialogContent dir="rtl" className="max-w-xl">
                <DialogHeader><DialogTitle className="text-right">تسجيل مستفيد خيري</DialogTitle></DialogHeader>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
                  <div><Label>الاسم الكامل</Label>
                    <Input data-testid="charity-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div><Label>رقم الهوية</Label>
                    <Input data-testid="charity-form-id" value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} required />
                  </div>
                  <div><Label>رقم الجوال</Label>
                    <Input data-testid="charity-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                  </div>
                  <div><Label>الجنسية</Label>
                    <Input data-testid="charity-form-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} required />
                  </div>
                  <div><Label>الجنس</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="charity-form-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">ذكر</SelectItem>
                        <SelectItem value="female">أنثى</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>الحملة</Label>
                    <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
                      <SelectTrigger data-testid="charity-form-campaign"><SelectValue placeholder="اختر الحملة" /></SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="submit" data-testid="charity-form-save"
                      className="bg-emerald-900 hover:bg-emerald-800 text-white">تسجيل</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="p-4 mb-4 border-emerald-900/10">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="charity-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث بالاسم، الهوية، أو الجوال" className="pr-9" />
          </div>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger data-testid="charity-campaign-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الحملات</SelectItem>
              {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-emerald-900/10 overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-16 text-center">
            <HandHeart className="h-10 w-10 text-emerald-900/30 mx-auto mb-3" />
            <p className="text-slate-500">لا يوجد مستفيدون بعد.</p>
          </div>
        ) : (
          <table className="w-full text-right min-w-[800px]" data-testid="charity-beneficiaries-table">
            <thead className="bg-emerald-950 text-white">
              <tr>
                <th className="p-3 font-heading">الاسم</th>
                <th className="p-3 font-heading">رقم الهوية</th>
                <th className="p-3 font-heading">الجوال</th>
                <th className="p-3 font-heading">الجنس</th>
                <th className="p-3 font-heading">الجنسية</th>
                <th className="p-3 font-heading">الحملة</th>
                <th className="p-3 font-heading">التسجيل رقم</th>
                <th className="p-3 font-heading w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, i) => (
                <tr key={b.id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="p-3 font-medium text-emerald-950">{b.name}</td>
                  <td className="p-3 tabular-nums">{b.national_id}</td>
                  <td className="p-3 tabular-nums">{b.phone}</td>
                  <td className="p-3">{b.gender === "male" ? "ذكر" : "أنثى"}</td>
                  <td className="p-3">{b.nationality}</td>
                  <td className="p-3">{campaignName(b.campaign_id)}</td>
                  <td className="p-3">
                    <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900">
                      #{b.occurrence}
                    </span>
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" data-testid={`charity-ben-delete-${b.id}`}
                      onClick={() => remove(b.id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
